import { jsx, jsxs } from "react/jsx-runtime";
import { createElement, useMemo } from "react";
import { createPortal } from "react-dom";
import { Grid } from "@mui/material";
import { Field, useFormikContext } from "formik";
import { isFunction } from "lodash";
import { createField } from "../../utils/helpers/createField.js";
import { getIsRequiredFromFunction } from "../../utils/helpers/getIsRequired.js";
import { FormHelpersProvider, useFormHelpers } from "../../utils/hooks/useFormHelpers.js";
import { FormValueProvider, useFormValue } from "../../utils/hooks/useFormValue.js";
import { SubmitCountProvider } from "../../utils/hooks/useSubmitCount.js";
import { Container } from "./form-layout.styles.js";
import "../address-field/address-field.js";
import "react-i18next";
import "../../utils/helpers/getHasError.js";
import "../../utils/hooks/useAddressSuggestions.js";
import "use-debounce";
import "../../utils/helpers/googlePlaces.js";
import "../../utils/helpers/places.js";
import "../../utils/shared/TextField.js";
import "../../utils/shared/TextFieldLabel.js";
import "../../utils/yup/main.js";
import "yup";
import "../../utils/fields/personalInformation.js";
import "../../../../../type/Appointment.js";
import "../masked-date-input/masked-date-input.js";
import "react-text-mask";
import "../../utils/entities/Form.js";
import "../../../ana-toolbox/ana-toolbox.js";
import "../../../../../constants/date/date.js";
import "date-fns/fp/format";
import "date-fns/isValid";
import "date-fns/parse";
import "date-fns";
import "../../utils/helpers/toBoolean.js";
import "../../utils/helpers/createFields.js";
import "../../utils/helpers/resetFields.js";
import "../../utils/yup/yup.js";
import "../../../../../utils/pipe.js";
import "date-fns/fp";
import "libphonenumber-js/max";
import "libphonenumber-js/mobile";
import "../../utils/helpers/predicates.js";
import "../../utils/yup/emailValidation.js";
import "../../utils/fields/contactDetails.js";
import "../../utils/helpers/featureFlags.js";
import "../../utils/helpers/getAgeFromBirthday.js";
import "../../utils/yup/buildValidationSchema.js";
import "../../utils/fields/miscellaneous.js";
import "../../utils/fields/insurance.js";
import "../../utils/fields/allergiesAndMedications.js";
import "../../../../../type/Disease.js";
import "../../utils/config/medicaments.js";
import "../../utils/fields/wishesAndExpectations.js";
import "../../utils/fields/consents.js";
import "../../utils/fields/profileInterest.js";
import "../field-helper-text/field-helper-text.js";
import "../translate/translate.js";
import "../checkbox-field/checkbox-field.js";
import "../../utils/shared/Checkbox.js";
import "../checkbox-field/checkbox-field.styles.js";
import "../../utils/shared/FormControlLabel.js";
import "../date-picker-field/date-picker-field.js";
import "@mui/x-date-pickers/StaticDatePicker/index.js";
import "date-fns/fp/formatWithOptions";
import "date-fns/locale/de";
import "date-fns/locale/en-US";
import "../../utils/helpers/date.js";
import "date-fns/fp/startOfMonth";
import "date-fns/fp/subYears";
import "../date-picker-field/date-picker-field.styles.js";
import "@mui/x-date-pickers";
import "../phone-field/phone-field.js";
import "react-window";
import "@mui/icons-material/ArrowDropDown.js";
import "@mui/icons-material/Close.js";
import "libphonenumber-js";
import "../../utils/helpers/phoneMetadata.js";
import "../text-input/text-input.js";
import "../text-input/text-input.styles.js";
import "../phone-field/phone-field.styles.js";
import "@emotion/react";
import "../radio-group/radio-group.js";
import "use-deep-compare";
import "../../utils/shared/Radio.js";
import "../../utils/shared/RadioFormControl.js";
import "../../utils/shared/RadioFormControlLabel.js";
import "../../utils/shared/RadioFormLabel.js";
import "../../utils/shared/RadioGroup.js";
import "../select-field/select-field.js";
import "../select-input/select-input.js";
import "../select-input/select-input.styles.js";
import "../text-field/text-field.js";
import "../toggle-field/toggle-field.js";
import "../../utils/shared/ToggleContainer.js";
import "../../utils/shared/ToggleFormControlLabel.js";
const FormLayoutItem = ({ field, ...gridProps }) => {
  if (!field)
    return null;
  return /* @__PURE__ */ jsx(Grid, { item: true, ...gridProps, children: createField(field) });
};
const FormLayoutItemWithValues = ({ field, ...gridProps }) => {
  const { values } = useFormikContext();
  const canShow = useMemo(() => {
    if (!(field == null ? void 0 : field.visibleWhen))
      return true;
    return field.visibleWhen(values);
  }, [values, field]);
  const isRequired = useMemo(() => {
    if (typeof (field == null ? void 0 : field.isRequired) !== "function")
      return field == null ? void 0 : field.isRequired;
    return getIsRequiredFromFunction(values, field.isRequired);
  }, [values, field]);
  const optimizedField = useMemo(() => {
    return { ...field, isRequired };
  }, [field, isRequired]);
  if (!canShow)
    return null;
  return /* @__PURE__ */ jsx(FormLayoutItem, { field: optimizedField, ...gridProps });
};
const CustomFormLayoutItem = ({ render, ...gridProps }) => {
  const formValues = useFormValue();
  const formHelpers = useFormHelpers();
  const children = render({ values: formValues, formHelpers });
  if (!children)
    return null;
  return /* @__PURE__ */ jsx(Grid, { item: true, ...gridProps, children });
};
const TestHelper = () => {
  if (process.env.NODE_ENV !== "test")
    return null;
  return /* @__PURE__ */ jsx(Field, { children: ({ form }) => form.isValidating || form.isSubmitting ? /* @__PURE__ */ jsx("div", { "data-testid": "formik-test-helper", "aria-hidden": "true" }) : null });
};
const FormLayoutItemRenderer = (item) => {
  const { type, key } = item;
  if (type === "field") {
    if (isFunction(item.field.visibleWhen) || isFunction(item.field.isRequired))
      return /* @__PURE__ */ jsx(FormLayoutItemWithValues, { ...item }, item.field.name);
    return /* @__PURE__ */ jsx(FormLayoutItem, { ...item }, item.field.name);
  }
  if (type === "custom")
    return /* @__PURE__ */ createElement(CustomFormLayoutItem, { ...item, key });
  return null;
};
const FormLayout = ({ items, portalAttr, submitButton, spacingX, spacingY }) => {
  const portalNodes = portalAttr && document.querySelectorAll(`[data-attr="${portalAttr}"]`);
  return /* @__PURE__ */ jsx(SubmitCountProvider, { children: /* @__PURE__ */ jsx(FormValueProvider, { children: /* @__PURE__ */ jsx(FormHelpersProvider, { children: /* @__PURE__ */ jsxs(Container, { spacingX: spacingX || 3, spacingY: spacingY || 3, children: [
    items.map(FormLayoutItemRenderer),
    submitButton && portalNodes && portalNodes.length > 0 ? Array.from(portalNodes).map((node) => createPortal(submitButton, node)) : /* @__PURE__ */ jsx(Grid, { container: true, justifyContent: "center", item: true, xs: 12, children: submitButton }),
    /* @__PURE__ */ jsx(TestHelper, {})
  ] }) }) }) });
};
export {
  FormLayout,
  FormLayoutItemRenderer,
  TestHelper
};
